// Befehlspalette der Hülle (design-system §3.5, ⌘K): ein Feld für Seiten (Navigation der Manifeste), Datensätze
// (platform.search.query, nach Art gruppiert), Befehle der Module und der Hülle sowie den Standortwechsel (':').
// Darstellung und Tastatur liefert der Baustein CommandPalette aus dem UI-Kit; hier werden die Anbieter verdrahtet.
import {
  Bell,
  CalendarClock,
  FileText,
  History,
  ListChecks,
  LogOut,
  MapPin,
  Monitor,
  Moon,
  Package,
  PanelLeft,
  ScanBarcode,
  Search,
  Sparkles,
  Sun,
  Terminal,
  Users,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { matchPath, useLocation, useNavigate, type NavigateFunction } from 'react-router'
import type { OutputOf } from '@shared/contract'
import { searchQuery } from '@modules/platform/contract'
import type { ApiClient } from '../api/client'
import { useApiClient, useEntityLinks, useWebModules } from '../api/context'
import { canRunAction, hasFeature, isWorkLocation, passesGate } from '../api/permissions'
import { useSession } from '../api/session'
import type { SessionPayload } from '../api/types'
import { readStored, writeStored } from '../lib/storage'
import { setTheme } from '../lib/theme'
import type { WebModule } from '../module'
import { shell, useShellStore } from '../stores/shell'
import { CommandPalette, staticProvider, type CommandItem, type CommandProvider } from '../ui/CommandPalette'
import type { Navigation } from './nav'
import { resolveScan } from './scan'
import { signOut } from './signOut'
import { switchLocation } from './Switchers'

type SearchOutput = OutputOf<typeof searchQuery>
const NO_HITS: SearchOutput = { groups: [], total: 0 }
const SEARCH_DELAY_MS = 180
const RECENT_KEY = 'shell.recent'
const RECENT_MAX = 6

/**
 * Arten von Datensätzen, die die Hülle auch ohne Such-Renderer eines Moduls beim Namen nennt (Überschrift und
 * Symbol der Gruppe). Ein Renderer im Manifest (`searchResults`) geht vor; das Ziel kommt sonst aus `entityLinks`.
 */
export const KNOWN_RECORD_KINDS: Readonly<Record<string, { label: string; icon: CommandItem['icon'] }>> = {
  item: { label: 'Artikel', icon: Package },
  partner: { label: 'Kunden & Lieferanten', icon: Users },
  document: { label: 'Belege', icon: FileText },
  lot: { label: 'Chargen', icon: CalendarClock },
  location: { label: 'Standorte', icon: MapPin },
}

const NO_PAGE = 'noch ohne eigene Seite'

interface RecentEntry {
  id: string
  title: string
  subtitle?: string
  code?: string
  to: string
}

const readRecent = (): RecentEntry[] => {
  const stored = readStored<unknown>(RECENT_KEY, [])
  return Array.isArray(stored)
    ? stored.filter((entry): entry is RecentEntry => typeof entry?.id === 'string' && typeof entry?.to === 'string')
    : []
}

/**
 * Eine Anfrage je Suchbegriff für alle Trefferarten, leicht verzögert (nicht jede Taste fragt den Server).
 * Bricht die Palette ab (nächste Taste), wird die Anfrage gar nicht erst gestellt.
 */
export function createSearchSource(client: ApiClient, delayMs = SEARCH_DELAY_MS) {
  let cache: { term: string; promise: Promise<SearchOutput> } | null = null
  return (term: string, signal: AbortSignal): Promise<SearchOutput> => {
    if (cache?.term === term) return cache.promise
    const promise = new Promise<SearchOutput>((resolve, reject) => {
      const timer = setTimeout(() => {
        client.run(searchQuery, { q: term, limit: 6 }).then(resolve, reject)
      }, delayMs)
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer)
          if (cache?.promise === promise) cache = null
          resolve(NO_HITS)
        },
        { once: true },
      )
    })
    promise.catch(() => {
      if (cache?.promise === promise) cache = null
    })
    cache = { term, promise }
    return promise
  }
}

/**
 * Liegt das Ziel auf einer Seite, deren Funktionsschalter aus ist (Lagerbeleg ohne Bestandsführung)? Solche Treffer und
 * zuletzt verwendeten Einträge bietet die Palette nicht an – die Seite zeigte nur „Funktion ausgeschaltet“. Ziele ohne
 * passende Route bleiben, wie sie sind.
 */
export function switchedOffTarget(modules: readonly WebModule[], session: Pick<SessionPayload, 'tenant'> | null) {
  const routes = modules.flatMap((module) => module.routes)
  return (to: string | null | undefined): boolean => {
    if (!to) return false
    const path = to.split(/[?#]/)[0] ?? to
    const route = routes.find((entry) => matchPath(entry.path, path) !== null)
    return route !== undefined && !hasFeature(session, route.feature)
  }
}

export interface PaletteContext {
  modules: readonly WebModule[]
  navigation: Navigation
  session: SessionPayload | null
  client: ApiClient
  entityHref: (type: string, id: string) => string | null
  /** wechselt die Seite und merkt den Eintrag unter „Zuletzt verwendet“ */
  open: (entry: RecentEntry) => void
  navigate: NavigateFunction
}

/** Anbieter der Palette aus Manifesten und Sitzung. */
export function buildProviders(context: PaletteContext): CommandProvider[] {
  const { modules, navigation, session, client, entityHref, open, navigate } = context
  const providers: CommandProvider[] = []

  // ---- Seiten ----
  const sectionLabel = (key: string): string | undefined => navigation.groups.find((group) => group.key === key)?.label
  providers.push(
    staticProvider({
      id: 'pages',
      heading: 'Seiten',
      priority: 10,
      items: navigation.all.map((item): CommandItem => ({
        id: item.key,
        kind: 'page',
        title: item.label,
        subtitle: sectionLabel(item.section),
        icon: item.icon as CommandItem['icon'],
        perform: () => open({ id: item.key, title: item.label, subtitle: sectionLabel(item.section), to: item.to }),
      })),
    }),
  )

  // ---- Datensätze: eine Gruppe je Trefferart (Renderer der Module, sonst bekannte Arten), dazu „Weitere Treffer“ ----
  if (canRunAction(session, searchQuery, 'any')) {
    const source = createSearchSource(client)
    const switchedOff = switchedOffTarget(modules, session)
    const renderers = new Map<string, WebModule['searchResults'][string]>()
    for (const module of modules)
      for (const [entity, renderer] of Object.entries(module.searchResults)) renderers.set(entity, renderer)
    const kinds = new Map<string, { label: string; icon: CommandItem['icon'] }>(Object.entries(KNOWN_RECORD_KINDS))
    // Schlüssel mit Unterart ('document:invoice') bilden keine eigene Gruppe: Sie wählen nur Ziel und Symbol des Treffers
    for (const [entity, renderer] of renderers)
      if (!entity.includes(':')) kinds.set(entity, { label: renderer.label, icon: renderer.icon as CommandItem['icon'] })
    for (const [entity, kind] of kinds) {
      const general = renderers.get(entity)
      providers.push({
        id: `search:${entity}`,
        heading: kind.label,
        priority: 20,
        minChars: 2,
        search: async (term, { signal }) =>
          (await source(term, signal)).groups
            .filter((group) => group.entity === entity)
            .flatMap((group) => group.hits)
            .flatMap((hit): CommandItem[] => {
              // Unterart zuerst (Belege: die Belegart → Seite des Moduls, dem sie gehört), dann die Art
              const key = hit.kind ? `${hit.entity}:${hit.kind}` : hit.entity
              const specific = hit.kind ? renderers.get(key) : undefined
              const renderer = specific ?? general
              const to = renderer ? renderer.to(hit) : entityHref(key, hit.id)
              if (switchedOff(to)) return []
              const id = `${hit.entity}:${hit.id}`
              return [
                {
                  id,
                  kind: 'record',
                  title: hit.title,
                  subtitle: hit.subtitle,
                  code: hit.code,
                  icon: specific ? (specific.icon as CommandItem['icon']) : kind.icon,
                  perform: () => {
                    if (to) open({ id, title: hit.title, subtitle: kind.label, code: hit.code, to })
                  },
                  disabledReason: to ? undefined : [hit.subtitle, NO_PAGE].filter(Boolean).join(' · '),
                },
              ]
            }),
      })
    }
    providers.push({
      id: 'search:other',
      heading: 'Weitere Treffer',
      priority: 30,
      minChars: 2,
      search: async (term, { signal }) =>
        (await source(term, signal)).groups
          .filter((group) => !kinds.has(group.entity))
          .flatMap((group) =>
            group.hits.flatMap((hit): CommandItem[] => {
              const to = entityHref(hit.kind ? `${hit.entity}:${hit.kind}` : hit.entity, hit.id)
              if (switchedOff(to)) return []
              const id = `${hit.entity}:${hit.id}`
              // ohne angemeldeten Suchanbieter nennt der Server nur den technischen Namen der Art – der bleibt weg
              const kind = group.title !== group.entity ? group.title : undefined
              const subtitle = [kind, hit.subtitle].filter(Boolean).join(' · ') || undefined
              return [
                {
                  id,
                  kind: 'record',
                  title: hit.title,
                  subtitle,
                  code: hit.code,
                  icon: Search,
                  perform: () => {
                    if (to) open({ id, title: hit.title, subtitle: kind, code: hit.code, to })
                  },
                  disabledReason: to ? undefined : [subtitle, NO_PAGE].filter(Boolean).join(' · '),
                },
              ]
            }),
          ),
    })
  }

  // ---- Befehle: der Module und der Hülle ----
  const moduleCommands = modules.flatMap((module) =>
    module.commands
      .filter((command) => passesGate(session, command, 'any'))
      .map((command): CommandItem => ({
        id: command.id,
        kind: 'command',
        title: command.label,
        keywords: command.keywords ? [...command.keywords] : undefined,
        icon: (command.icon ?? Terminal) as CommandItem['icon'],
        perform: () => open({ id: command.id, title: command.label, to: command.to }),
      })),
  )
  // prettier-ignore
  const shellCommands: CommandItem[] = [
    { id: 'shell.scan', kind: 'command', title: 'Scannen', keywords: ['Barcode', 'Kamera', 'EAN'], icon: ScanBarcode, perform: () => shell.setScan(true) },
    { id: 'shell.notifications', kind: 'command', title: 'Benachrichtigungen anzeigen', keywords: ['Glocke', 'Meldungen'], icon: Bell, perform: () => shell.openNotifications('new') },
    { id: 'shell.tasks', kind: 'command', title: 'Aufgaben anzeigen', keywords: ['zu erledigen', 'offen'], icon: ListChecks, perform: () => shell.openNotifications('tasks') },
    { id: 'shell.assistant', kind: 'command', title: 'KI-Assistent öffnen', hotkey: 'mod+j', icon: Sparkles, perform: () => shell.setAssistant(true) },
    { id: 'shell.sidebar', kind: 'command', title: 'Seitenleiste ein- oder ausklappen', hotkey: 'mod+b', icon: PanelLeft, perform: () => shell.toggleSidebar() },
    { id: 'shell.theme.light', kind: 'setting', title: 'Darstellung: Hell', keywords: ['Theme', 'Design'], icon: Sun, perform: () => setTheme('light') },
    { id: 'shell.theme.dark', kind: 'setting', title: 'Darstellung: Dunkel', keywords: ['Theme', 'Design', 'Nacht'], icon: Moon, perform: () => setTheme('dark') },
    { id: 'shell.theme.system', kind: 'setting', title: 'Darstellung: Wie das System', keywords: ['Theme', 'Design', 'automatisch'], icon: Monitor, perform: () => setTheme('system') },
    {
      id: 'shell.logout',
      kind: 'command',
      title: 'Abmelden',
      keywords: ['Logout', 'ausloggen'],
      icon: LogOut,
      // ein Abmelde-Wächter (Kasse: Bons warten) sperrt mit einem Hinweis statt abzumelden
      perform: async () => {
        await signOut(() => void navigate('/login', { replace: true }))
      },
    },
  ]
  providers.push(
    staticProvider({
      id: 'commands',
      heading: 'Befehle',
      prefix: '>',
      priority: 40,
      items: [...moduleCommands, ...shellCommands],
      limit: 8,
    }),
  )

  // ---- Standort wechseln ----
  const locations = (session?.locations ?? []).filter(isWorkLocation)
  if (locations.length > 1) {
    const all = session?.locations ?? []
    providers.push(
      staticProvider({
        id: 'locations',
        heading: 'Standort wechseln',
        prefix: ':',
        priority: 50,
        limit: 12,
        items: [
          {
            id: 'location:all',
            kind: 'setting',
            title: 'Alle Standorte',
            icon: MapPin,
            perform: () => switchLocation(null, all),
          },
          ...locations.map((location): CommandItem => ({
            id: `location:${location.id}`,
            kind: 'setting',
            title: location.name,
            code: location.code,
            icon: MapPin,
            meta: location.id === session?.activeLocationId ? 'aktuell' : undefined,
            perform: () => switchLocation(location.id, all),
          })),
        ],
      }),
    )
  }
  return providers
}

export function ShellPalette({ navigation }: { navigation: Navigation }) {
  const open = useShellStore((state) => state.paletteOpen)
  const initialQuery = useShellStore((state) => state.paletteQuery)
  const modules = useWebModules()
  const session = useSession()
  const client = useApiClient()
  const entityHref = useEntityLinks()
  const navigate = useNavigate()
  const location = useLocation()
  const [recent, setRecent] = useState<RecentEntry[]>(readRecent)
  // nach einem Scan ohne eindeutiges Ziel beginnt die Palette neu mit dem gelesenen Code als Suche
  const [scanRound, setScanRound] = useState(0)

  const openEntry = useCallback(
    (entry: RecentEntry) => {
      setRecent((current) => {
        const next = [entry, ...current.filter((other) => other.id !== entry.id)].slice(0, RECENT_MAX)
        writeStored(RECENT_KEY, next)
        return next
      })
      void navigate(entry.to)
    },
    [navigate],
  )

  const providers = useMemo(
    () => buildProviders({ modules, navigation, session, client, entityHref, open: openEntry, navigate }),
    [modules, navigation, session, client, entityHref, openEntry, navigate],
  )
  const recentItems = useMemo<CommandItem[]>(
    () =>
      recent.filter((entry) => !switchedOffTarget(modules, session)(entry.to)).map((entry) => ({
        id: entry.id,
        kind: 'page',
        title: entry.title,
        subtitle: entry.subtitle,
        code: entry.code,
        icon: History,
        perform: () => openEntry(entry),
      })),
    [recent, openEntry, modules, session],
  )

  return (
    <CommandPalette
      key={scanRound}
      open={open}
      onOpenChange={shell.setPalette}
      providers={providers}
      recent={recentItems}
      initialQuery={initialQuery}
      placeholder="Suchen oder Befehl eingeben …"
      locationId={session?.activeLocationId ?? null}
      route={location.pathname}
      onAskAssistant={(query) => shell.setAssistant(true, query)}
      onScan={(result) => {
        // Der Scanner hat seinen Code samt Enter in das Suchfeld getippt; das Enter kommt nicht bei der Liste an.
        const targets = resolveScan(result, modules, session)
        if (targets.length === 1 && targets[0]) {
          shell.setPalette(false)
          void navigate(targets[0].to)
          return
        }
        // kein eindeutiges Ziel: Die Suche zeigt die Treffer zum Code – frisch, damit der erste Treffer markiert ist
        shell.openPalette(result.code)
        setScanRound((round) => round + 1)
      }}
    />
  )
}
